import {
  createChatSession,
  getChatSessions,
  getChatSession,
  updateChatSession,
  deleteChatSession,
  createMessage,
  getAvailableModels,
  getLLMConfig,
  getSessionPrompts,
  linkEntityPrompt,
  unlinkPrompt,
  updatePromptContent
} from '@/services/chatApi'

export const createSession = async ({ commit }, { orgId, name, llmConfig = null }) => {
  console.log('Creating new chat session:', name, 'with LLM config:', llmConfig)

  try {
    const sessionData = await createChatSession(orgId, name || 'New Chat', llmConfig)

    // Transform API response to match store format (using session_uuid as session_code)
    const session = {
      session_code: sessionData.session_uuid,
      id: sessionData.id,
      session_uuid: sessionData.session_uuid,
      name: sessionData.name,
      organization_id: sessionData.organization_id,
      member_id: sessionData.member_id,
      created_at: sessionData.created_at,
      updated_at: sessionData.updated_at,
      llm_config: sessionData.llm_config
    }

    commit('CREATE_SESSION', { orgId, session })
    commit('SET_ACTIVE_SESSION', session.session_code)

    return session
  } catch (error) {
    console.error('Failed to create chat session:', error)
    throw error
  }
}

export const fetchSessions = async ({ commit }, { orgId }) => {
  console.log('Fetching chat sessions for org:', orgId)

  try {
    const response = await getChatSessions(orgId)

    // Transform sessions to match store format (using session_uuid)
    const sessions = response.sessions.map(s => ({
      session_code: s.session_uuid,
      id: s.id,
      session_uuid: s.session_uuid,
      name: s.name,
      organization_id: s.organization_id,
      member_id: s.member_id,
      created_at: s.created_at,
      updated_at: s.updated_at
    }))

    // Clear existing sessions for this org and add new ones
    sessions.forEach(session => {
      commit('CREATE_SESSION', { orgId, session })
    })

    return sessions
  } catch (error) {
    console.error('Failed to fetch chat sessions:', error)
    throw error
  }
}

export const fetchSession = async ({ commit }, { orgId, sessionUuid }) => {
  console.log('Fetching chat session:', sessionUuid)

  try {
    const sessionData = await getChatSession(orgId, sessionUuid)

    const session = {
      session_code: sessionData.session_uuid,
      id: sessionData.id,
      session_uuid: sessionData.session_uuid,
      name: sessionData.name,
      organization_id: sessionData.organization_id,
      member_id: sessionData.member_id,
      created_at: sessionData.created_at,
      updated_at: sessionData.updated_at,
      llm_config: sessionData.llm_config
    }

    commit('CREATE_SESSION', { orgId: session.organization_id, session })

    // Handle messages if they exist in the response
    if (sessionData.messages && sessionData.messages.length > 0) {
      const messages = sessionData.messages.map(m => ({
        message_code: m.message_uuid,
        message_uuid: m.message_uuid,
        session_code: session.session_code,
        role: m.role,
        content: m.content,
        created_at: m.created_at,
        updated_at: m.updated_at,
        message_metadata: m.message_metadata || null
      }))

      commit('SET_MESSAGES', { sessionCode: session.session_code, messages })
    }

    return session
  } catch (error) {
    console.error('Failed to fetch chat session:', error)
    throw error
  }
}

export const updateSession = async ({ commit, state }, { orgId, sessionUuid, updates }) => {
  console.log('Updating chat session:', sessionUuid, updates)

  try {
    const sessionData = await updateChatSession(orgId, sessionUuid, updates)

    const session = {
      session_code: sessionData.session_uuid,
      id: sessionData.id,
      session_uuid: sessionData.session_uuid,
      name: sessionData.name,
      organization_id: sessionData.organization_id,
      member_id: sessionData.member_id,
      created_at: sessionData.created_at,
      updated_at: sessionData.updated_at
    }

    commit('CREATE_SESSION', { orgId: session.organization_id, session })

    return session
  } catch (error) {
    console.error('Failed to update chat session:', error)
    throw error
  }
}

export const deleteSession = async ({ commit }, { orgId, sessionCode }) => {
  console.log('Deleting chat session:', sessionCode)

  try {
    // sessionCode is now the session_uuid (UUID string)
    await deleteChatSession(orgId, sessionCode)

    commit('DELETE_SESSION', { orgId, sessionCode })
  } catch (error) {
    console.error('Failed to delete chat session:', error)
    throw error
  }
}

export const setActiveSession = ({ commit }, sessionCode) => {
  commit('SET_ACTIVE_SESSION', sessionCode)
}

export const sendMessage = async ({ commit, state }, { orgId, sessionCode, content, role = 'user', reportUuid = null, reportVersion = null }) => {
  console.log('Sending message to session:', sessionCode, content, reportUuid ? `(editing report ${reportUuid} v${reportVersion})` : '')

  try {
    // sessionCode is the session_uuid (UUID string)
    const messageData = await createMessage(orgId, sessionCode, content, reportUuid, reportVersion)

    // Transform API response to match store format
    const message = {
      message_code: messageData.message_uuid,
      message_uuid: messageData.message_uuid,
      session_code: sessionCode,
      role: messageData.role,
      content: messageData.content,
      created_at: messageData.created_at,
      updated_at: messageData.updated_at
    }

    // Don't commit here - let WebSocket broadcast handle it
    // This prevents duplicates when both REST API and WebSocket add the same message
    console.log('Message sent successfully via API:', message)

    return message
  } catch (error) {
    console.error('Failed to send message:', error)
    throw error
  }
}

export const updateMessage = ({ commit }, { sessionCode, messageCode, updates }) => {
  commit('UPDATE_MESSAGE', { sessionCode, messageCode, updates })
}

export const clearChatData = ({ commit }) => {
  commit('CLEAR_CHAT_DATA')
}

export const fetchAvailableModels = async ({ commit }, { orgId }) => {
  console.log('Fetching available models for org:', orgId)

  try {
    const response = await getAvailableModels(orgId)
    commit('SET_AVAILABLE_MODELS', response.models)
    return response.models
  } catch (error) {
    console.error('Failed to fetch available models:', error)
    throw error
  }
}

export const fetchLLMConfig = async ({ commit }, { orgId, sessionUuid }) => {
  console.log('Fetching LLM config for session:', sessionUuid)

  try {
    const config = await getLLMConfig(orgId, sessionUuid)
    return config
  } catch (error) {
    console.error('Failed to fetch LLM config:', error)
    throw error
  }
}

// ==================== Prompt Management Actions ====================

export const fetchSessionPrompts = async ({ commit }, { orgId, sessionUuid }) => {
  console.log('Fetching prompts for session:', sessionUuid)

  try {
    const prompts = await getSessionPrompts(orgId, sessionUuid)
    commit('SET_SESSION_PROMPTS', { sessionCode: sessionUuid, prompts })
    return prompts
  } catch (error) {
    console.error('Failed to fetch session prompts:', error)
    throw error
  }
}

export const linkProjectPromptAction = async ({ commit, dispatch }, { orgId, sessionUuid, projectId }) => {
  console.log('Linking project prompt:', projectId, 'to session:', sessionUuid)

  try {
    await linkEntityPrompt(orgId, sessionUuid, 'project', projectId)
    // Refresh prompts to get updated list
    await dispatch('fetchSessionPrompts', { orgId, sessionUuid })
  } catch (error) {
    console.error('Failed to link project prompt:', error)
    throw error
  }
}

export const linkLayerPromptAction = async ({ commit, dispatch }, { orgId, sessionUuid, layerId }) => {
  console.log('Linking layer prompt:', layerId, 'to session:', sessionUuid)

  try {
    await linkEntityPrompt(orgId, sessionUuid, 'layer', layerId)
    // Refresh prompts to get updated list
    await dispatch('fetchSessionPrompts', { orgId, sessionUuid })
  } catch (error) {
    console.error('Failed to link layer prompt:', error)
    throw error
  }
}

export const unlinkPromptAction = async ({ commit }, { orgId, sessionUuid, promptId, entityType }) => {
  console.log('Unlinking prompt:', promptId, 'from session:', sessionUuid)

  try {
    await unlinkPrompt(orgId, sessionUuid, promptId)
    commit('REMOVE_PROMPT_LINK', { sessionCode: sessionUuid, entityType, promptId })
  } catch (error) {
    console.error('Failed to unlink prompt:', error)
    throw error
  }
}

export const updatePromptContentAction = async ({ commit }, { orgId, sessionUuid, promptId, content }) => {
  console.log('Updating prompt content:', promptId)

  try {
    await updatePromptContent(orgId, sessionUuid, promptId, content)
    // Update across all sessions in the store
    commit('UPDATE_PROMPT_CONTENT', { promptId, newContent: content })
  } catch (error) {
    console.error('Failed to update prompt content:', error)
    throw error
  }
}
